/**
 * The seller's placement and click history (canon APP-006).
 *
 * The same three disciplines as every other seller read apply, and each is a canon rule
 * rather than a preference.
 *
 * **The session decides whose history this is.** The request carries no parameter at all —
 * no seller id, no deal id, no date — so there is nothing a caller could vary to reach
 * another founder's placements (`contracts/API.md` § Seller placement history shape). The
 * cross-seller guarantee here is an absence, not a check.
 *
 * **A response body is untrusted input.** Every field is parsed rather than cast. A row this
 * app cannot read fails the panel instead of being skipped: a founder shown a short history
 * would reasonably conclude a placement never ran, and "this didn't load" is the true
 * statement.
 *
 * **Nothing is derived from a row that core did not send.** A rank is core's, an amount is
 * core's, and a click figure is the qualified count the weekly/monthly lists rank on
 * (canon § 11). This module reads them; it never infers a rank from a position in the list
 * or a cost from a source.
 */
import { apiRequest } from "./client";
import { INVALID, asRecord, malformed, nonEmptyString, optionalCount, requiredCount } from "./parse";
import type {
  ClicksNote,
  LifetimeClicks,
  PlacementSource,
  SellerPlacement,
  SellerPlacementHistory,
} from "./types";

const PLACEMENT_SOURCES = new Set<string>(["auction", "rollover", "honorable", "founding"]);

/**
 * An unrecognised source fails the parse instead of degrading to a default.
 *
 * The source is the whole point of a history row: it is the difference between a position a
 * founder paid for, one they were given at no additional charge, and a listing that was
 * never in the Top 10 at all. Guessing it would misstate what their credit bought, which is
 * exactly the question this panel exists to answer.
 */
function isPlacementSource(value: string): value is PlacementSource {
  return PLACEMENT_SOURCES.has(value);
}

/**
 * The clicks note. Required, not optional.
 *
 * Canon § 1 promises no sales, leads or impression count, and `contracts/API.md` ships the
 * qualifying sentence with the number so that no surface can show one without the other —
 * the same rule that makes `terms` mandatory on a credit balance. A body carrying click
 * figures and no note is therefore not a body this app may render.
 */
function parseClicksNote(value: unknown): ClicksNote | null {
  const row = asRecord(value);
  if (row === null) return null;
  const code = nonEmptyString(row.code);
  const message = nonEmptyString(row.message);
  if (code === null || message === null) return null;
  return { code, message };
}

/**
 * The lifetime click figures (`APP-021`, canon decision `D-018`).
 *
 * Degrades to `null` rather than to a wrong number. A partial object — one figure readable and
 * the other not — is `null` too: showing a founder "143 ranking clicks" beside a silently
 * missing wall figure would be a claim about their record that this app cannot support, and
 * `D-018` point 6 turns on the two being read together.
 *
 * Zero is a value, not an absence. A founder with no Founding deal has `0` wall clicks, and
 * that is a true statement about their record rather than a reason to hide the line.
 */
function parseLifetimeClicks(value: unknown): LifetimeClicks | null {
  const row = asRecord(value);
  if (row === null) return null;

  const rankingClicks = requiredCount(row.rankingClicks);
  const foundingWallClicks = requiredCount(row.foundingWallClicks);
  if (rankingClicks === INVALID || foundingWallClicks === INVALID) return null;

  return { rankingClicks, foundingWallClicks };
}

/**
 * One placement row.
 *
 * `rank` and `winningBidCents` are nullable *and* meaningful: `null` is "this concept does
 * not apply to this row", which the panel renders as the absence of a position rather than
 * as a zero. A rank of `0` is refused along with a negative one — canon § 7 ranks from
 * position 1, so `0` is not a position core would send and not a position to render.
 */
export function parseSellerPlacement(value: unknown): SellerPlacement | null {
  const row = asRecord(value);
  if (row === null) return null;

  const placementId = nonEmptyString(row.placementId);
  const displayDate = nonEmptyString(row.displayDate);
  const source = nonEmptyString(row.source);
  const qualifiedClicks = requiredCount(row.qualifiedClicks);
  if (
    placementId === null ||
    displayDate === null ||
    source === null ||
    !isPlacementSource(source) ||
    qualifiedClicks === INVALID
  ) {
    return null;
  }

  const rank = optionalCount(row.rank);
  const winningBidCents = optionalCount(row.winningBidCents);
  if (rank === INVALID || winningBidCents === INVALID) return null;
  if (rank !== null && rank < 1) return null;

  const deal = asRecord(row.deal);
  if (deal === null) return null;
  const id = nonEmptyString(deal.id);
  const slug = nonEmptyString(deal.slug);
  const productName = nonEmptyString(deal.productName);
  // A row that cannot name its deal cannot be rendered honestly: the founder would be told
  // that *something* of theirs ran, without being told what.
  if (id === null || slug === null || productName === null) return null;

  return {
    placementId,
    displayDate,
    source,
    rank,
    winningBidCents,
    qualifiedClicks,
    deal: { id, slug, productName },
  };
}

/** Returns `null` when the body is not a placement history. */
export function parseSellerPlacements(body: unknown): SellerPlacementHistory | null {
  const row = asRecord(body);
  if (row === null) return null;

  const clicks = parseClicksNote(row.clicks);
  const serverTime = nonEmptyString(row.serverTime);
  const timeZone = nonEmptyString(row.timeZone);
  if (clicks === null || serverTime === null || timeZone === null) return null;
  // Canon § 8 fixes Dealbid's day to UTC, and every date on this panel is formatted in UTC.
  // A body claiming some other zone is one this app cannot render honestly — it would put a
  // founder's placement on the wrong day — so it is refused rather than silently formatted
  // as if it had said UTC.
  if (timeZone !== "UTC") return null;

  if (!Array.isArray(row.placements)) return null;
  const placements: SellerPlacement[] = [];
  for (const entry of row.placements) {
    const placement = parseSellerPlacement(entry);
    if (placement === null) return null;
    placements.push(placement);
  }

  return { placements, lifetime: parseLifetimeClicks(row.lifetime), clicks, timeZone, serverTime };
}

/**
 * Every placement of the signed-in seller's deals, newest display day first.
 *
 * The order is core's and is preserved as received. It is a total order (display date, then
 * rank, then placement id), so two reads answer in the same sequence and a founder does not
 * see their history rearrange itself between visits.
 */
export async function fetchSellerPlacements(): Promise<SellerPlacementHistory> {
  const history = parseSellerPlacements(await apiRequest<unknown>("/v1/seller/placements"));
  if (history === null) throw malformed();
  return history;
}
